// views/Financeiro/lib/useEtiquetasUc.ts
//
// Carrega o catálogo de etiquetas e os vínculos com as UCs, e grava as mudanças
// (docs/superpowers/specs/2026-10-13-etiquetas-uc-design.md). Depois de gravar, atualiza a
// memória local — a lista de faturas não recarrega.
//
// As duas tabelas são pequenas e lidas inteiras. O RLS só devolve linhas para adm, gerente
// e analista; para os outros papéis o hook nem consulta (`ativo` = false).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../src/lib/supabase';
import {
  contarUcsPorEtiqueta,
  indexarEtiquetasPorUc,
  limparDadosEtiqueta,
  type DadosEtiqueta,
  type Etiqueta,
  type VinculoEtiqueta,
} from '../../../src/lib/etiquetasUc';

const PAPEIS_ETIQUETA = ['adm', 'gerente', 'analista'];

/** Quem vê e mexe nas etiquetas — o mesmo que as policies de `etiquetas` deixam. */
export function podeUsarEtiquetas(role: string | null | undefined): boolean {
  return PAPEIS_ETIQUETA.includes(role || '');
}

/** Mensagem que a pessoa entende, a partir do erro do PostgREST. */
function erroLegivel(error: { code?: string; message?: string } | null, padrao: string): Error {
  if (error?.code === '23505') return new Error('Já existe uma etiqueta com esse texto.');
  if (error?.code === '42501') return new Error('Seu perfil não pode mexer em etiquetas.');
  return new Error(error?.message ? `${padrao}: ${error.message}` : padrao);
}

const PAGINA = 1000;

async function lerVinculos(): Promise<VinculoEtiqueta[]> {
  // O PostgREST corta em 1000 linhas: pagina até vir uma página incompleta.
  const todos: VinculoEtiqueta[] = [];
  for (let de = 0; ; de += PAGINA) {
    const { data, error } = await supabase
      .from('uc_etiquetas')
      .select('uc_id, etiqueta_id, aplicada_em')
      .order('uc_id', { ascending: true })
      .order('etiqueta_id', { ascending: true })
      .range(de, de + PAGINA - 1);
    if (error) throw erroLegivel(error, 'Não foi possível carregar as etiquetas das UCs');
    todos.push(...((data || []) as VinculoEtiqueta[]));
    if (!data || data.length < PAGINA) return todos;
  }
}

export type EtiquetasUcApi = ReturnType<typeof useEtiquetasUc>;

export function useEtiquetasUc(ativo: boolean) {
  const [etiquetas, setEtiquetas] = useState<Etiqueta[]>([]);
  const [vinculos, setVinculos] = useState<VinculoEtiqueta[]>([]);
  const [erroCarga, setErroCarga] = useState<string | null>(null);

  const recarregar = useCallback(async () => {
    if (!ativo) return;
    try {
      const [{ data, error }, todosVinculos] = await Promise.all([
        supabase.from('etiquetas').select('id, texto, cor, descricao').order('texto', { ascending: true }),
        lerVinculos(),
      ]);
      if (error) throw erroLegivel(error, 'Não foi possível carregar as etiquetas');
      setEtiquetas((data || []) as Etiqueta[]);
      setVinculos(todosVinculos);
      setErroCarga(null);
    } catch (e) {
      setErroCarga(e instanceof Error ? e.message : String(e));
    }
  }, [ativo]);

  useEffect(() => { recarregar(); }, [recarregar]);

  const porUc = useMemo(() => indexarEtiquetasPorUc(etiquetas, vinculos), [etiquetas, vinculos]);
  const ucsPorEtiqueta = useMemo(() => contarUcsPorEtiqueta(vinculos), [vinculos]);

  const criar = useCallback(async (dados: DadosEtiqueta): Promise<Etiqueta> => {
    const { data, error } = await supabase
      .from('etiquetas')
      .insert(limparDadosEtiqueta(dados))
      .select('id, texto, cor, descricao')
      .single();
    if (error || !data) throw erroLegivel(error, 'Não foi possível criar a etiqueta');
    const nova = data as Etiqueta;
    setEtiquetas(prev => [...prev, nova].sort((a, b) => a.texto.localeCompare(b.texto, 'pt-BR')));
    return nova;
  }, []);

  const editar = useCallback(async (id: string, dados: DadosEtiqueta): Promise<void> => {
    const { data, error } = await supabase
      .from('etiquetas')
      .update(limparDadosEtiqueta(dados))
      .eq('id', id)
      .select('id, texto, cor, descricao')
      .single();
    if (error || !data) throw erroLegivel(error, 'Não foi possível salvar a etiqueta');
    const editada = data as Etiqueta;
    setEtiquetas(prev => prev.map(e => (e.id === id ? editada : e)).sort((a, b) => a.texto.localeCompare(b.texto, 'pt-BR')));
  }, []);

  const apagar = useCallback(async (id: string): Promise<void> => {
    // O banco apaga os vínculos junto (on delete cascade).
    const { error } = await supabase.from('etiquetas').delete().eq('id', id);
    if (error) throw erroLegivel(error, 'Não foi possível apagar a etiqueta');
    setEtiquetas(prev => prev.filter(e => e.id !== id));
    setVinculos(prev => prev.filter(v => v.etiqueta_id !== id));
  }, []);

  const aplicar = useCallback(async (ucId: number, etiquetaId: string): Promise<void> => {
    const { data, error } = await supabase
      .from('uc_etiquetas')
      .insert({ uc_id: ucId, etiqueta_id: etiquetaId })
      .select('uc_id, etiqueta_id, aplicada_em')
      .single();
    // Já estava na UC (outra pessoa colocou antes): o resultado é o mesmo, não é erro.
    if (error?.code === '23505') { await recarregar(); return; }
    if (error || !data) throw erroLegivel(error, 'Não foi possível colocar a etiqueta');
    setVinculos(prev => [...prev, data as VinculoEtiqueta]);
  }, [recarregar]);

  const tirar = useCallback(async (ucId: number, etiquetaId: string): Promise<void> => {
    const { error } = await supabase.from('uc_etiquetas').delete().eq('uc_id', ucId).eq('etiqueta_id', etiquetaId);
    if (error) throw erroLegivel(error, 'Não foi possível tirar a etiqueta');
    setVinculos(prev => prev.filter(v => !(v.uc_id === ucId && v.etiqueta_id === etiquetaId)));
  }, []);

  return { etiquetas, porUc, ucsPorEtiqueta, erroCarga, recarregar, criar, editar, apagar, aplicar, tirar };
}
